import React from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline } from '@atlaskit/primitives';
import { type SearchResultCommitPartial } from '@atlassian/search-client';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';

import { messages } from './messages';

interface CommitSearchResultProps {
	partial: SearchResultCommitPartial;
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
	isAllProductSelected?: boolean;
}

export const CommitSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isAllProductSelected,
}: CommitSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { lastModifiedDate, url, permissionLevel, title, excerpt, entity } = partial;
	const { author, container } = entity || {};
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	let messageDescription;
	if (author?.user) {
		messageDescription = (
			<Inline space="space.025" alignBlock="start">
				<ProfileAvatar name={author.user.name} picture={author.user.picture} />
				<Box>{processExcerpt(excerpt, true, `${author.user.name}: `)}</Box>
			</Inline>
		);
	} else {
		messageDescription = <DescriptionBody description={processExcerpt(excerpt, true)} />;
	}

	const headerMetadataParts = [
		label,
		['repository-name', container?.name ?? null],
		['padlock', padlockIcon],
		['last-modified', lastModifiedComponent],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={messageDescription}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
			footer={
				<MentionedByLinkedResults
					linkedResults={partial.linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={partial}
					url={url}
					position={position}
					onResultClicked={onResultClicked || (() => { })}
					onResultContextMenu={onResultCtxMenuClicked}
					isAllProductSelected={isAllProductSelected}
				/>
			}
		/>
	);
};
